import { useDeferredValue, useEffect, useState } from "react";

const SEARCH_DEBOUNCE_MS = 200;

/**
 * Delays the term the column search actually runs on.
 *
 * Fuzzy matching over the full column set costs tens of milliseconds on the largest tables, which is
 * long enough to be felt between keystrokes. Two layers, because they solve different problems: the
 * debounce collapses a typing burst into a single scan, and `useDeferredValue` keeps that scan off
 * the urgent render so the input commits and paints first. Deferring alone would not be enough — the
 * scan is one synchronous call inside a `useMemo`, and React can only yield between components.
 *
 * Clearing bypasses both — the term is reset programmatically when the category changes, and lagging
 * there would show one frame of the new category filtered by the old term.
 */
export const useAppliedSearchTerm = (searchTerm: string): string => {
    const [debouncedTerm, setDebouncedTerm] = useState(searchTerm);

    useEffect(() => {
        if (searchTerm === "") {
            setDebouncedTerm("");
            return;
        }

        const timeoutId = window.setTimeout(() => setDebouncedTerm(searchTerm), SEARCH_DEBOUNCE_MS);

        return () => window.clearTimeout(timeoutId);
    }, [searchTerm]);

    const deferredTerm = useDeferredValue(debouncedTerm);

    return debouncedTerm === "" ? "" : deferredTerm;
};
